import {
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";

import { db } from "../firebase";
import { checkEarlyFanBadge } from "./milestoneService";

export async function checkFollowing({ followerId, creatorId }) {
  if (!followerId || !creatorId) return false;

  const followId = `${followerId}_${creatorId}`;
  const followSnap = await getDoc(doc(db, "follows", followId));

  return followSnap.exists();
}

async function syncFollowingArray(followerId, creatorId, remove) {
  try {
    await updateDoc(doc(db, "users", followerId), {
      following: remove ? arrayRemove(creatorId) : arrayUnion(creatorId),
    });
  } catch (err) {
    console.error("following array update failed:", err);
  }
}

/**
 * Toggle a follow. Source of truth is follows/{followerId}_{creatorId}.
 * users/{followerId}.following is a denormalized copy written independently.
 * users/{creatorId}.followerCount is maintained by onFollowCreated / onFollowDeleted.
 */
export async function toggleFollow({
  followerId,
  creatorId,
  creatorUsername = "",
  creatorDisplayName = "",
  creatorAvatar = "",
  isFollowing,
}) {
  if (!followerId || !creatorId) {
    throw new Error("Missing follow information.");
  }
  if (followerId === creatorId) {
    throw new Error("You can't follow yourself.");
  }

  const followId = `${followerId}_${creatorId}`;
  const followRef = doc(db, "follows", followId);

  if (isFollowing) {
    const arrayWrite = syncFollowingArray(followerId, creatorId, true);
    try {
      await deleteDoc(followRef);
    } catch (err) {
      console.error("follows delete failed:", err);
      throw err;
    }
    await arrayWrite;
    return false;
  }

  const arrayWrite = syncFollowingArray(followerId, creatorId, false);
  try {
    await setDoc(followRef, {
      followerId,
      creatorId,
      creatorUsername,
      creatorDisplayName,
      creatorAvatar,
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.error("follows create failed:", err);
    throw err;
  }
  await arrayWrite;

  try {
    const creatorSnap = await getDoc(doc(db, "users", creatorId));
    const count = Number(creatorSnap.exists() ? creatorSnap.data()?.followerCount || 0 : 0);
    await checkEarlyFanBadge(followerId, count);
  } catch (err) {
    console.error("early fan badge failed:", err);
  }

  return true;
}

export function followingIdsFromUser(user) {
  return Array.isArray(user?.following) ? user.following.filter(Boolean) : [];
}

export function isFollowingListPrivate(user) {
  return user?.followingListPrivate === true;
}

export async function setFollowingListPrivate(userId, value) {
  if (!userId) throw new Error("Not signed in.");
  await updateDoc(doc(db, "users", userId), {
    followingListPrivate: Boolean(value),
  });
}
